import { useEffect, useMemo, useState } from "react";
import { Workflow, ChevronUp, ChevronDown, RotateCcw, Save, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { invalidateAppSettingsCache } from "@/hooks/useAppSettings";

/**
 * Workflows & Statuses editor.
 *
 * What's editable:
 *  - Display labels for each canonical stage (label aliasing only).
 *  - Display ORDER of stages.
 *
 * What's NOT editable here:
 *  - Adding or removing real stages — backend code matches on canonical
 *    strings everywhere, so renaming/removing them would break things.
 *
 * The Portal / PO Inbox cards are still shown read-only because their
 * canonical lifecycles are coupled to specific code paths (e.g. PO has
 * 'parsing' → 'parsed' transitions driven by the parser).
 */

// Canonical stages MUST mirror backend ORDER_STATUSES / DOCKET_STATUSES.
const CANONICAL_PIPELINE = ["Orders In", "Picking", "To Invoice", "Invoiced", "Paid", "Completed"];
const CANONICAL_DOCKETS  = ["Pending", "Scheduled", "Picked Up", "Cancelled"];

const PORTAL_STAGES = ["Orders In", "Picking", "To Invoice", "Completed"];
// PO Inbox uses internal canonical keys but displays friendlier labels.
const PO_STAGES = [
  { key: "new",          label: "Awaiting Review" },
  { key: "parsing",      label: "Parsing" },
  { key: "approved",     label: "Approved" },
  { key: "rejected",     label: "Rejected" },
  { key: "parse_error",  label: "Parse Failed" },
];

const STAGE_TONE = {
  // Pipeline
  "Orders In": "blue", "Picking": "amber", "To Invoice": "purple",
  "Invoiced": "indigo", "Paid": "emerald", "Completed": "green",
  // Dockets
  "Pending": "amber", "Scheduled": "blue", "Picked Up": "green", "Cancelled": "red",
  // PO (canonical keys)
  "new": "amber", "parsing": "blue", "approved": "green",
  "rejected": "red", "parse_error": "red",
};
const TONE_CLASS = {
  blue: "bg-blue-100 text-blue-800",
  amber: "bg-amber-100 text-amber-800",
  purple: "bg-purple-100 text-purple-800",
  indigo: "bg-indigo-100 text-indigo-800",
  emerald: "bg-emerald-100 text-emerald-800",
  green: "bg-green-100 text-green-800",
  red: "bg-red-100 text-red-800",
  zinc: "bg-zinc-100 text-zinc-800",
};

function ReadOnlyStageRow({ stages, labels }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {stages.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${TONE_CLASS[STAGE_TONE[s] || "zinc"]}`}>
            {labels?.[s] || s}
          </span>
          {i < stages.length - 1 && <span className="text-zinc-300 select-none">→</span>}
        </div>
      ))}
    </div>
  );
}

/** Editable list — rename + reorder. Validates non-empty labels on the fly. */
function EditableWorkflow({ title, description, canonical, order, labels, onChange, testidPrefix }) {
  const move = (idx, delta) => {
    const next = [...order];
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange({ order: next, labels });
  };
  const setLabel = (canonicalName, value) => {
    const next = { ...labels };
    if (value.trim()) next[canonicalName] = value;
    else delete next[canonicalName];
    onChange({ order, labels: next });
  };
  const resetAll = () => onChange({ order: [...canonical], labels: {} });
  const isDirty = useMemo(() => {
    if (order.join("|") !== canonical.join("|")) return true;
    return Object.keys(labels || {}).length > 0;
  }, [order, labels, canonical]);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3" data-testid={`${testidPrefix}-editor`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-semibold text-zinc-900">{title}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">{description}</p>
        </div>
        {isDirty && (
          <button
            type="button"
            onClick={resetAll}
            className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
            data-testid={`${testidPrefix}-reset`}
            title="Reset to defaults"
          ><RotateCcw className="w-3 h-3" /> Reset</button>
        )}
      </div>

      <ul className="divide-y divide-zinc-100 border border-zinc-100 rounded-lg overflow-hidden">
        {order.map((canonicalName, idx) => {
          const tone = TONE_CLASS[STAGE_TONE[canonicalName] || "zinc"];
          const customised = !!(labels && labels[canonicalName]);
          return (
            <li
              key={canonicalName}
              className="flex items-center gap-2 px-3 py-2 bg-white"
              data-testid={`${testidPrefix}-row-${idx}`}
            >
              <div className="flex flex-col gap-0.5">
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="w-6 h-5 rounded text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-30"
                  aria-label="Move up"
                  data-testid={`${testidPrefix}-up-${idx}`}
                ><ChevronUp className="w-3.5 h-3.5 mx-auto" /></button>
                <button
                  type="button"
                  onClick={() => move(idx, 1)}
                  disabled={idx === order.length - 1}
                  className="w-6 h-5 rounded text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-30"
                  aria-label="Move down"
                  data-testid={`${testidPrefix}-down-${idx}`}
                ><ChevronDown className="w-3.5 h-3.5 mx-auto" /></button>
              </div>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${tone}`}>
                {canonicalName}
              </span>
              <span className="text-zinc-300">→</span>
              <input
                type="text"
                placeholder={canonicalName}
                value={labels?.[canonicalName] || ""}
                onChange={(e) => setLabel(canonicalName, e.target.value)}
                maxLength={40}
                className="flex-1 h-9 px-2.5 text-sm border border-zinc-200 rounded-md focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                data-testid={`${testidPrefix}-label-${idx}`}
                aria-label={`Display label for ${canonicalName}`}
              />
              {customised && (
                <button
                  type="button"
                  onClick={() => setLabel(canonicalName, "")}
                  className="p-1 rounded text-zinc-400 hover:text-zinc-900"
                  aria-label="Clear custom label"
                  title="Use default label"
                  data-testid={`${testidPrefix}-clear-${idx}`}
                ><X className="w-3.5 h-3.5" /></button>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-[11px] text-zinc-500 leading-relaxed">
        Drag-free reordering: use the chevrons. The badges on the left are the
        canonical names used internally — they cannot be removed. The text
        input on the right is what's shown in the UI (leave blank to use the
        canonical name).
      </p>
    </div>
  );
}

/** Rename-only editor — used for workflows where order is fixed (Portal, PO). */
function RenameOnlyWorkflow({ title, description, items, labels, defaultLabelFor, onChange, testidPrefix }) {
  const setLabel = (key, value) => {
    const next = { ...labels };
    if (value.trim()) next[key] = value;
    else delete next[key];
    onChange(next);
  };
  const resetAll = () => onChange({});
  const isDirty = Object.keys(labels || {}).length > 0;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3" data-testid={`${testidPrefix}-editor`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-semibold text-zinc-900">{title}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">{description}</p>
        </div>
        {isDirty && (
          <button
            type="button"
            onClick={resetAll}
            className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
            data-testid={`${testidPrefix}-reset`}
            title="Reset to defaults"
          ><RotateCcw className="w-3 h-3" /> Reset</button>
        )}
      </div>
      <ul className="divide-y divide-zinc-100 border border-zinc-100 rounded-lg overflow-hidden">
        {items.map((it, idx) => {
          const key = typeof it === "string" ? it : it.key;
          const canonicalLabel = typeof it === "string" ? it : it.label;
          const tone = TONE_CLASS[STAGE_TONE[key] || STAGE_TONE[canonicalLabel] || "zinc"];
          const customised = !!(labels && labels[key]);
          return (
            <li key={key} className="flex items-center gap-2 px-3 py-2 bg-white" data-testid={`${testidPrefix}-row-${idx}`}>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${tone}`}>
                {canonicalLabel}
              </span>
              <span className="text-zinc-300">→</span>
              <input
                type="text"
                placeholder={defaultLabelFor?.(key) || canonicalLabel}
                value={labels?.[key] || ""}
                onChange={(e) => setLabel(key, e.target.value)}
                maxLength={40}
                className="flex-1 h-9 px-2.5 text-sm border border-zinc-200 rounded-md focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                data-testid={`${testidPrefix}-label-${idx}`}
                aria-label={`Display label for ${canonicalLabel}`}
              />
              {customised && (
                <button
                  type="button"
                  onClick={() => setLabel(key, "")}
                  className="p-1 rounded text-zinc-400 hover:text-zinc-900"
                  aria-label="Clear custom label"
                  title="Use default label"
                  data-testid={`${testidPrefix}-clear-${idx}`}
                ><X className="w-3.5 h-3.5" /></button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function WorkflowsSection() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pipelineOrder, setPipelineOrder] = useState(CANONICAL_PIPELINE);
  const [pipelineLabels, setPipelineLabels] = useState({});
  const [docketsOrder, setDocketsOrder] = useState(CANONICAL_DOCKETS);
  const [docketsLabels, setDocketsLabels] = useState({});
  const [portalLabels, setPortalLabels] = useState({});
  const [poLabels, setPoLabels] = useState({});
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const loadFromServer = (data) => {
    const orderP = (data?.Order_Stages || []).filter((s) => CANONICAL_PIPELINE.includes(s));
    const finalP = orderP.length === CANONICAL_PIPELINE.length ? orderP : [...CANONICAL_PIPELINE];
    const orderD = (data?.Docket_Statuses || []).filter((s) => CANONICAL_DOCKETS.includes(s));
    const finalD = orderD.length === CANONICAL_DOCKETS.length ? orderD : [...CANONICAL_DOCKETS];
    setPipelineOrder(finalP);
    setDocketsOrder(finalD);
    setPipelineLabels(data?.Order_Stage_Labels || {});
    setDocketsLabels(data?.Docket_Status_Labels || {});
    setPortalLabels(data?.Portal_Order_Stage_Labels || {});
    setPoLabels(data?.PO_Status_Labels || {});
    setSavedSnapshot({
      pO: finalP, pL: data?.Order_Stage_Labels || {},
      dO: finalD, dL: data?.Docket_Status_Labels || {},
      poL: data?.Portal_Order_Stage_Labels || {},
      poiL: data?.PO_Status_Labels || {},
    });
  };

  useEffect(() => {
    setLoading(true);
    api.get("/settings")
      .then(({ data }) => loadFromServer(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = useMemo(() => {
    if (!savedSnapshot) return false;
    return (
      pipelineOrder.join("|") !== savedSnapshot.pO.join("|") ||
      docketsOrder.join("|") !== savedSnapshot.dO.join("|") ||
      JSON.stringify(pipelineLabels) !== JSON.stringify(savedSnapshot.pL) ||
      JSON.stringify(docketsLabels) !== JSON.stringify(savedSnapshot.dL) ||
      JSON.stringify(portalLabels) !== JSON.stringify(savedSnapshot.poL) ||
      JSON.stringify(poLabels) !== JSON.stringify(savedSnapshot.poiL)
    );
  }, [pipelineOrder, pipelineLabels, docketsOrder, docketsLabels, portalLabels, poLabels, savedSnapshot]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings", {
        Order_Stages: pipelineOrder,
        Order_Stage_Labels: pipelineLabels,
        Docket_Statuses: docketsOrder,
        Docket_Status_Labels: docketsLabels,
        Portal_Order_Stage_Labels: portalLabels,
        PO_Status_Labels: poLabels,
      });
      loadFromServer(data);
      invalidateAppSettingsCache();
      toast.success("Workflow updates saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const revert = () => {
    if (savedSnapshot) {
      setPipelineOrder([...savedSnapshot.pO]);
      setPipelineLabels({ ...savedSnapshot.pL });
      setDocketsOrder([...savedSnapshot.dO]);
      setDocketsLabels({ ...savedSnapshot.dL });
      setPortalLabels({ ...savedSnapshot.poL });
      setPoLabels({ ...savedSnapshot.poiL });
    }
  };

  return (
    <section className="es-card overflow-hidden" data-testid="workflows-section">
      <div className="px-6 py-4 border-b border-zinc-100 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-brand-50 ring-1 ring-brand-200 flex items-center justify-center flex-shrink-0">
          <Workflow className="w-4 h-4 text-brand-700" />
        </div>
        <div className="flex-1">
          <h2 className="font-semibold text-zinc-900">Workflows & Statuses</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Rename and reorder pipeline + docket stages. Display only — the
            canonical names are still used internally so existing data and
            integrations keep working.
          </p>
        </div>
        {isDirty && (
          <div className="flex items-center gap-2" data-testid="workflows-dirty-actions">
            <button
              type="button"
              onClick={revert}
              className="text-xs font-semibold text-zinc-600 hover:text-zinc-900 px-3 py-1.5 rounded-md hover:bg-zinc-100"
              data-testid="workflows-revert"
            >Revert</button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="es-btn-primary disabled:opacity-60"
              data-testid="workflows-save"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? "Saving…" : "Save workflow changes"}
            </button>
          </div>
        )}
      </div>
      <div className="px-6 py-5 space-y-4">
        {loading ? (
          <div className="text-xs font-mono text-zinc-500 py-4">Loading workflows…</div>
        ) : (
          <>
            <EditableWorkflow
              title="Order Pipeline (internal)"
              description="Lifecycle on the Pipeline Kanban — admin + staff side."
              canonical={CANONICAL_PIPELINE}
              order={pipelineOrder}
              labels={pipelineLabels}
              onChange={({ order, labels }) => { setPipelineOrder(order); setPipelineLabels(labels); }}
              testidPrefix="workflow-pipeline"
            />
            <EditableWorkflow
              title="Dispatch Dockets"
              description="Lifecycle of every pickup / delivery docket."
              canonical={CANONICAL_DOCKETS}
              order={docketsOrder}
              labels={docketsLabels}
              onChange={({ order, labels }) => { setDocketsOrder(order); setDocketsLabels(labels); }}
              testidPrefix="workflow-dockets"
            />

            <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3 hidden" data-testid="workflow-portal-readonly" />

            <RenameOnlyWorkflow
              title="Reseller Portal — Order View"
              description="What resellers see in their portal. Falls back to the Pipeline labels above when no Portal-specific alias is set."
              items={PORTAL_STAGES}
              labels={portalLabels}
              defaultLabelFor={(k) => pipelineLabels?.[k] || k}
              onChange={setPortalLabels}
              testidPrefix="workflow-portal"
            />

            <RenameOnlyWorkflow
              title="Purchase Orders Inbox"
              description="System-managed lifecycle — rename only. The internal status keys never change, only how the inbox renders them."
              items={PO_STAGES}
              labels={poLabels}
              defaultLabelFor={(k) => PO_STAGES.find((s) => s.key === k)?.label || k}
              onChange={setPoLabels}
              testidPrefix="workflow-po"
            />
          </>
        )}
      </div>
    </section>
  );
}
